import React from "react";

import { useWatch } from "react-hook-form";

import usePostHouseholdMember from "@fartherfinance/frontend/api/Households/hooks/usePostHouseholdMember";

import { useHouseholdContext } from "../../context";
import { formatAddHouseholdMemberResponse } from "../../utils";
import ButtonPrimary from "@src/multiCustodian/components/MUI/Button/Button";
import { useAdvisorPageInit } from "@src/multiCustodian/hooks/Advisor/useAdvisorPageInit";
import useStatusNotification from "@src/multiCustodian/hooks/useStatusNotification";
import Text from "@src/sharedComponents/Typography/Text";
import { handleApiError } from "@src/utils/handleApiError";

import { HouseholdMemberRelationshipFormField } from "./HouseholdMemberRelationshipFormField";

import styles from "./HouseholdProfile.module.css";

export default function AddHouseholdMember() {
  const { form, householdId, onSave, onCancel } = useHouseholdContext();
  const {
    control,
    formState: { isValid },
  } = form;

  const newHouseholdMember = useWatch({
    control,
    name: "newMember",
  });

  const memberOption = useWatch({
    control,
    name: "newMember.memberOption",
  });

  const relationshipOption = useWatch({
    control,
    name: "newMember.relationshipOption",
  });

  const isAddButtonDisabled = !memberOption || !relationshipOption;
  const isSaveButtonDisabled = !form.formState.isDirty;

  const { auth } = useAdvisorPageInit();

  const notify = useStatusNotification();

  const { mutateAsync: addHouseholdMember } = usePostHouseholdMember(auth!);

  const handleAddMemberClick = async () => {
    if (!householdId || !newHouseholdMember) {
      return;
    }

    try {
      if (!isValid) {
        throw new Error("Please fill out all required fields to proceed");
      }
      await addHouseholdMember({
        householdId,
        body: formatAddHouseholdMemberResponse(newHouseholdMember),
      });
    } catch (error) {
      handleApiError({
        error: error,
        errorMessage: "Error adding household contact",
        genericErrorMessage: "Error adding household contact",
        notify,
      });
    }
  };

  const handleSave = async () => {
    if (onSave) {
      const formData = form.getValues();
      await onSave(formData);
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
  };

  return (
    <div className={styles.addContactContainer}>
      <Text className={styles.addContactTitle} variant="title3">
        Add Contact
      </Text>
      <HouseholdMemberRelationshipFormField
        memberName="newMember.memberOption"
        relationshipName="newMember.relationshipOption"
        pointOfContactName="newMember.pointOfContact"
        className={styles.addContactFormField}
        memberLabel="New Contact"
        customAction={
          <ButtonPrimary
            text="Add New Contact"
            onClick={handleAddMemberClick}
            variant="outlined"
            buttonType="secondary"
            className={styles.addContactBtn}
            disabled={isAddButtonDisabled}
          />
        }
      />
      <div className={styles.footerActions}>
        <ButtonPrimary
          text="Cancel"
          onClick={handleCancel}
          variant="outlined"
          buttonType="secondary"
        />
        <ButtonPrimary
          text="Save"
          onClick={handleSave}
          variant="contained"
          buttonType="primary"
          disabled={isSaveButtonDisabled}
          className={styles.saveButton}
        />
      </div>
    </div>
  );
}
